import { format as dfFormat, isValid as isValidDate, parse, parseISO } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

/** Валидация IANA тайзоны */
function isValidTimeZone(tz?: string | null): tz is string {
    if (!tz) return false;
    try {
        // бросит RangeError при неверном идентификаторе
        new Intl.DateTimeFormat('en-US', { timeZone: tz });
        return true;
    } catch {
        return false;
    }
}

export function formatToDateObject(
    value: number | string | number[] | string[]
): Date | undefined | Date[] {
    if (!value) return undefined;
    if (Array.isArray(value)) return value.map((v) => new Date(v));
    return new Date(value);
}

/** Универсальный парсер во входную Date */
function toDate(input: any): Date | null {
    if (input == null) return null;
    if (input instanceof Date) return isValidDate(input) ? input : null;


    // число: поддержим секунды (частая причина Invalid Date)
    if (typeof input === 'number') {
        const ms = input < 1e12 ? input * 1000 : input; // если это секунды, домножим
        const d = new Date(ms);
        return isValidDate(d) ? d : null;
    }

    if (typeof input === 'string') {
        const s = input.trim();
        if (!s) return null;

        // если это формат dd.MM.yyyy HH:mm
        const custom = parse(s, 'dd.MM.yyyy HH:mm', new Date());
        if (isValidDate(custom)) return custom;

        // если ISO
        if (/[T]/.test(s) || /^\d{4}-\d{2}-\d{2}/.test(s)) {
            try {
                const d = parseISO(s);
                return isValidDate(d) ? d : null;
            } catch { }
        }

        // fallback
        const d = new Date(s);
        return isValidDate(d) ? d : null;
    }

    // последнее средство
    const d = new Date(input);
    return isValidDate(d) ? d : null;
}

/** Безопасный форматтер: tz → date-fns-tz, иначе локаль; вернёт null при любой проблеме */
function fmt(date: any, pattern: string, tz?: string | null): string | null {
    const d = toDate(date);
    if (!d) return null;

    try {
        if (isValidTimeZone(tz)) {
            return formatInTimeZone(d, tz, pattern);
        }
        return dfFormat(d, pattern);
    } catch (e) {
        // сюда попадём при редких кейсах: неожиданный тип, битая дата и т.п.
        console.warn('format error:', e, { date, parsed: d, tz });
        return null;
    }
}

export interface FormatInUserTimeZoneOptions {
    /**
     * Тайзона, в которой задано входное "настенное" время.
     * Если не указана — считаем, что date уже корректный UTC-момент.
     */
    fromTimeZone?: string | null;

    /**
     * Тайзона пользователя. Если не указана — определяем через detectUserTimeZone().
     */
    userTimeZone?: string | null;
}

/**
 * Универсальный форматтер "из локального времени источника → во время пользователя".
 * - если передан fromTimeZone, сначала интерпретирует вход как локальное время в этой зоне (zonedTimeToUtc)
 * - затем форматирует в тайзоне пользователя (userTimeZone или auto-detect).
 */
export function formatInUserTimeZone(
    date: any,
    pattern: string,
    options?: FormatInUserTimeZoneOptions
): string | null {
    const userTz = options?.userTimeZone ?? detectUserTimeZone();

    let utcDate: Date | null = null;

    if (options?.fromTimeZone && isValidTimeZone(options.fromTimeZone)) {
        try {
            // интерпретируем вход как локальное время в fromTimeZone и получаем UTC-момент
            const d = fromZonedTime(date, options.fromTimeZone);
            utcDate = isValidDate(d) ? d : null;
        } catch (e) {
            console.warn('formatInUserTimeZone fromZonedTime error:', e, {
                date,
                options,
            });
            return null;
        }
    } else {
        utcDate = toDate(date);
    }

    if (!utcDate) return null;

    try {
        if (isValidTimeZone(userTz)) {
            return formatInTimeZone(utcDate, userTz, pattern);
        }
        return dfFormat(utcDate, pattern);
    } catch (e) {
        console.warn('formatInUserTimeZone format error:', e, {
            date,
            parsed: utcDate,
            options,
            userTz,
        });
        return null;
    }
}

export function formatDateToLocalFormat(date: any, tz?: string | null) {
    return fmt(date, 'dd.MM.yyyy', tz);
}

export function formatDateToLocalFormatHHmmss(date: any, tz?: string | null) {
    return fmt(date, 'dd.MM.yyyy, HH:mm:ss', tz);
}

export function formatDateToLocalFormatHHmm(date: any, tz?: string | null) {
    return fmt(date, 'dd.MM.yyyy, HH:mm', tz);
}

export function formatDateToISO8601(date: any, tz?: string | null) {
    return fmt(date, 'yyyy-MM-dd', tz);
}

// ===== Bitrix / Moscow helpers =====

/** IANA-тайзона Москвы, используется Битриксом */
export const MOSCOW_TIMEZONE = 'Europe/Moscow';

/** Детектирует тайзону пользователя через Intl API */
export function detectUserTimeZone(): string | null {
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return isValidTimeZone(tz) ? tz : null;
    } catch {
        return null;
    }
}

/**
 * Парсит битриксовую дату/время в формате `dd.MM.yyyy HH:mm[:ss]` в "наивную"
 * локальную дату/время в ISO-подобном формате `yyyy-MM-dd'T'HH:mm:ss`
 * (без привязки к конкретной тайзоне).
 *
 * Привязка к тайзоне (Москва и т.п.) выполняется уже на уровне форматтера
 * через параметр fromTimeZone.
 */
export function parseBitrixDateTime(input: string | null | undefined): string | null {
    if (!input) return null;
    const trimmed = input.trim();
    if (!trimmed) return null;

    const [datePart, timePartRaw] = trimmed.split(' ');
    if (!datePart) return null;

    const [dd, mm, yyyy] = datePart.split('.');
    if (!dd || !mm || !yyyy) return null;

    let timePart = timePartRaw || '00:00:00';
    const parts = timePart.split(':');
    const hh = parts[0] || '00';
    const mi = parts[1] || '00';
    const ss = parts[2] || '00';

    // Формируем "наивную" ISO-подобную строку без смещения
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}`;
}

/**
 * Форматирует битриксовое московское время в локальное время пользователя (часы и минуты).
 * Пример: "04.12.2025 09:47:49" (Москва) → "13:47" в Томске.
 */
export function formatBitrixMoscowToUserTimeHHmm(input: string | null | undefined, userTz?: string | null): string {
    if (!input) return '';
    const date = parseBitrixDateTime(input);
    if (!date) return input;

    const formatted = formatInUserTimeZone(date, 'HH:mm', {
        fromTimeZone: MOSCOW_TIMEZONE,
        userTimeZone: userTz,
    });
    return formatted ?? input;
}

export function formatBitrixMoscowToUserDateTime(
    input: string | null | undefined,
    userTz?: string | null
): string {
    if (!input) return '';
    const date = parseBitrixDateTime(input);
    if (!date) return input;

    const formatted = formatInUserTimeZone(date, 'dd.MM.yyyy HH:mm', {
        fromTimeZone: MOSCOW_TIMEZONE,
        userTimeZone: userTz,
    });
    return formatted ?? input;
}

/**
 * Форматтер для списка тикетов:
 * - если дата (в часовом поясе пользователя) совпадает с сегодняшней — показываем только время (HH:mm)
 * - иначе показываем только дату (dd.MM.yyyy)
 */
export function formatBitrixMoscowToUserDateOrTimeShort(
    input: string | null | undefined,
    userTz?: string | null
): string {
    if (!input) return '—';
    const date = parseBitrixDateTime(input);
    if (!date) return input;

    const baseOptions = {
        fromTimeZone: MOSCOW_TIMEZONE,
        userTimeZone: userTz,
    } as const;

    const localDate = formatInUserTimeZone(date, 'dd.MM.yyyy', baseOptions);
    if (!localDate) return input;

    const localTime = formatInUserTimeZone(date, 'HH:mm', baseOptions) ?? '';
    const todayStr = dfFormat(new Date(), 'dd.MM.yyyy');

    return localDate === todayStr && localTime ? localTime : localDate;
}
